import * as React from 'react'

import { UploadIcon } from 'lucide-react'
import { inject, observer } from 'mobx-react'

import { FilePicker } from '~/client/src/shared/components/FilePicker'
import ImageInputStore from '~/client/src/shared/components/ImageInput/ImageInput.store'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Field,
  FieldError,
  FieldTitle,
} from '~/client/src/shared/components/shadcn-ui/field'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { FileUploadingStore } from '~/client/src/shared/stores/domain/FileUploading.store'

const ACCEPT_PATTERN = 'image/*'

interface IProps {
  imageUrl: string
  projectId: string
  label: string
  alt: string
  onChange: (newUrl: string) => void
  fileUploadingStore?: FileUploadingStore
  maxFileSize?: number
  maxWidth?: number
  maxHeight?: number
  shoulOmitCreateLog?: boolean
}

const ProjectImageField: React.FC<IProps> = observer(
  ({
    imageUrl,
    projectId,
    label,
    alt,
    onChange,
    fileUploadingStore,
    maxFileSize,
    maxWidth,
    maxHeight,
    shoulOmitCreateLog,
  }: IProps) => {
    const onChangeRef = React.useRef(onChange)
    const storeRef = React.useRef<ImageInputStore>()

    onChangeRef.current = onChange

    if (!storeRef.current) {
      storeRef.current = new ImageInputStore(
        (newUrl: string) => onChangeRef.current(newUrl),
        fileUploadingStore as FileUploadingStore,
      )
    }

    const store = storeRef.current

    const uploadImage = async ([fileToUpload]: File[]) =>
      store.change(
        fileToUpload,
        projectId,
        maxFileSize,
        maxWidth,
        maxHeight,
        shoulOmitCreateLog,
      )

    return (
      <Field data-invalid={!!store.errorMessage}>
        <FieldTitle>{label}</FieldTitle>
        {imageUrl && (
          <div className="tw:flex tw:h-[90px] tw:w-[350px] tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-lg tw:border tw:border-border tw:bg-muted">
            <img
              alt={alt}
              src={imageUrl}
              className="tw:max-h-full tw:max-w-full tw:object-contain"
            />
          </div>
        )}
        <FilePicker
          accept={ACCEPT_PATTERN}
          disabled={store.isLoading}
          onPick={uploadImage}
          render={<Button variant="outline" />}
        >
          {store.isLoading ? (
            <span data-icon="inline-start">
              <Spinner size="xs" />
            </span>
          ) : (
            <UploadIcon data-icon="inline-start" />
          )}
          {store.isLoading
            ? Localization.translator.uploading
            : imageUrl
              ? Localization.translator.change_verb
              : Localization.translator.uploadFile}
        </FilePicker>
        <FieldError>{store.errorMessage}</FieldError>
      </Field>
    )
  },
)

export default inject('fileUploadingStore')(ProjectImageField)
